import React, { useEffect, useState } from 'react';

import { Loader } from '@/components/Loader';
import { Modal } from '@/components/Modal';
import { useAxios } from '@/hooks/useAxios';
import { queryClient } from '@/queryclient';
import { Technology } from '@/types';
import { cn } from '@/utils/classname';

interface ConstantineModalProps {
  open: boolean;
  onClose: () => void;
  technology: Technology | null;
}

export const ConstantineModal: React.FC<ConstantineModalProps> = ({
  open,
  onClose,
  technology,
}) => {
  const axios = useAxios();
  const [repoUrl, setRepoUrl] = useState('');
  const [goal, setGoal] = useState('');
  const [isGuessing, setIsGuessing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // When modal opens with a technology, guess the repo URL
  useEffect(() => {
    if (open && technology) {
      setRepoUrl('');
      setGoal('');
      setError('');
      setSuccess('');
      guessRepo();
    }
  }, [open, technology?.key]);

  const guessRepo = async () => {
    if (!technology) return;
    setIsGuessing(true);
    setError('');
    try {
      const { data } = await axios.post('/osint/guess-repo', {
        cpe: technology.cpe,
        technology_name:
          technology.name || `${technology.vendor} ${technology.product}`,
      });
      setRepoUrl(data.repo_url || '');
    } catch (err) {
      console.error('Failed to guess repo:', err);
      setError('Could not guess repository URL. Please enter it manually.');
    } finally {
      setIsGuessing(false);
    }
  };

  const handleSubmit = async () => {
    if (!repoUrl.trim()) {
      setError('Repository URL is required');
      return;
    }

    setIsSubmitting(true);
    setError('');
    try {
      await axios.post('/osint/submit', {
        repo_url: repoUrl.trim(),
        technology_key: technology?.key || undefined,
        goal: goal.trim() || undefined,
      });

      // Invalidate OSINT job queries to refetch and show the new queued job
      queryClient.invalidateQueries({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          return (
            queryKey[0] === 'MY' &&
            queryKey[1] === 'job' &&
            queryKey.includes('osint')
          );
        },
        refetchType: 'active',
      });

      // Also invalidate repository queries in case new repos were discovered
      queryClient.invalidateQueries({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          return (
            queryKey[0] === 'OSINT' &&
            queryKey[1] === 'repos' &&
            queryKey[2] === 'technology'
          );
        },
        refetchType: 'active',
      });

      setSuccess(
        'Constantine scan initiated. Check the OSINT tab for results.'
      );
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      const message =
        axiosErr?.response?.data?.message ||
        'Failed to submit. Please try again.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setRepoUrl('');
      setGoal('');
      setError('');
      setSuccess('');
      onClose();
    }
  };

  if (!open || !technology) return null;

  return (
    <Modal
      size="xl"
      title="Research with Constantine"
      subtitle={`Analyze ${technology.name || technology.product} for vulnerabilities`}
      open={open}
      onClose={handleClose}
      disableAutoFocus
      footer={{
        text: isSubmitting ? 'Submitting...' : 'Start Scan',
        onClick: handleSubmit,
        disabled: !repoUrl.trim() || isSubmitting || !!success,
      }}
    >
      <div className="space-y-4">
        {error && (
          <div
            className="bg-chariot-status-critical/10 text-chariot-status-critical rounded-md p-2 text-sm"
            role="alert"
          >
            {error}
          </div>
        )}

        {success && (
          <div
            className="bg-chariot-status-positive/10 text-chariot-status-positive rounded-md p-2 text-sm"
            role="status"
          >
            {success}
          </div>
        )}

        <div>
          <label
            htmlFor="constantine-repo-url"
            className="text-chariot-text-primary mb-1 block text-sm font-medium"
          >
            Repository URL
          </label>
          <div className="relative">
            <input
              id="constantine-repo-url"
              type="url"
              value={repoUrl}
              onChange={e => {
                setRepoUrl(e.target.value);
                if (error) setError('');
              }}
              placeholder={
                isGuessing
                  ? 'Guessing repository...'
                  : 'https://github.com/org/repo'
              }
              disabled={isGuessing || isSubmitting || !!success}
              className={cn(
                'border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary w-full rounded-md border px-3 py-2 pr-9 text-sm',
                'focus:border-chariot-layer-primary focus:ring-chariot-layer-primary focus:ring-1 focus:outline-none',
                'disabled:cursor-not-allowed disabled:opacity-50'
              )}
            />
            {isGuessing && (
              <div className="absolute top-1/2 right-3 -translate-y-1/2">
                <Loader isLoading type="spinner" />
              </div>
            )}
          </div>
        </div>

        <div>
          <label
            htmlFor="constantine-goal"
            className="text-chariot-text-primary mb-1 block text-sm font-medium"
          >
            Goal (Optional)
          </label>
          <input
            id="constantine-goal"
            type="text"
            value={goal}
            onChange={e => setGoal(e.target.value)}
            placeholder="e.g., Focus on SQL injection vulnerabilities"
            disabled={isSubmitting || !!success}
            className={cn(
              'border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary w-full rounded-md border px-3 py-2 text-sm',
              'focus:border-chariot-layer-primary focus:ring-chariot-layer-primary focus:ring-1 focus:outline-none',
              'disabled:cursor-not-allowed disabled:opacity-50'
            )}
          />
          <p className="text-chariot-text-secondary mt-1 text-xs">
            Injected into all LLM system prompts to focus the analysis
          </p>
        </div>
      </div>
    </Modal>
  );
};
